/**
 * The owner's controls over the cloud computer's screen while they hold it (/ordinateur, « Prendre les commandes »),
 * made for a phone but usable everywhere: « Clavier » opens the device's keyboard and types into the computer, « Coller »
 * sends the device's clipboard (then Ctrl+V), « Copier » puts what the computer copied on the device's clipboard, and a
 * row of keys a phone keyboard lacks (Ctrl and Alt stay down for the next key).
 */
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { IconCheck, IconCopy } from "./icons";
import type { ScreenHandle } from "./live-desktop";
import { Button, cx, Textarea } from "./ui";

/** X11 keysyms (X11/keysymdef.h). */
export const KEYSYM = {
  BackSpace: 0xff08,
  Tab: 0xff09,
  Return: 0xff0d,
  Escape: 0xff1b,
  Home: 0xff50,
  Left: 0xff51,
  Up: 0xff52,
  Right: 0xff53,
  Down: 0xff54,
  End: 0xff57,
  Delete: 0xffff,
  Control_L: 0xffe3,
  Alt_L: 0xffe9,
} as const;

/** The keysym of one typed character: Latin-1 as is, anything else as a Unicode keysym (0x01000000 + code point). */
export function charKeysym(ch: string): number | null {
  if (ch === "\n" || ch === "\r") return KEYSYM.Return;
  if (ch === "\t") return KEYSYM.Tab;
  const cp = ch.codePointAt(0);
  if (cp === undefined || cp < 0x20 || (cp >= 0x7f && cp < 0xa0)) return null;
  return cp < 0x100 ? cp : 0x01000000 + cp;
}

/** Keys of the hidden input sent as they are (the characters themselves arrive through its value). */
const SPECIAL_KEYS: Record<string, number> = {
  Enter: KEYSYM.Return,
  Tab: KEYSYM.Tab,
  Escape: KEYSYM.Escape,
  ArrowLeft: KEYSYM.Left,
  ArrowRight: KEYSYM.Right,
  ArrowUp: KEYSYM.Up,
  ArrowDown: KEYSYM.Down,
  Home: KEYSYM.Home,
  End: KEYSYM.End,
  Delete: KEYSYM.Delete,
};

/** The hidden input is emptied past this length (outside a composition): only the latest word needs diffing. */
const BUFFER_RESET = 64;
/** The computer's clipboard is set before Ctrl+V reaches the focused app. */
const PASTE_KEY_DELAY_MS = 150;

type Modifier = "ctrl" | "alt";

export function RemoteControls({ screen, remoteClipboard, className }: { screen: ScreenHandle | null; remoteClipboard: string | null; className?: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const typed = useRef("");
  const composing = useRef(false);
  const [keyboard, setKeyboard] = useState(false);
  const [mods, setMods] = useState<Record<Modifier, boolean>>({ ctrl: false, alt: false });
  const modsRef = useRef(mods);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pasteId = useId();
  const ready = screen !== null;

  useEffect(() => {
    modsRef.current = mods;
  }, [mods]);
  useEffect(() => () => {
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
  }, []);
  useEffect(() => setCopied(false), [remoteClipboard]);

  function flash(text: string) {
    setNotice(text);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 2_500);
  }

  /** One key with the sticky modifiers held around it (they are released after it). */
  function tap(keysym: number, extra: Partial<Record<Modifier, boolean>> = {}) {
    if (!screen) return;
    const held = { ctrl: modsRef.current.ctrl || Boolean(extra.ctrl), alt: modsRef.current.alt || Boolean(extra.alt) };
    if (held.ctrl) screen.sendKey(KEYSYM.Control_L, true);
    if (held.alt) screen.sendKey(KEYSYM.Alt_L, true);
    screen.sendKey(keysym);
    if (held.alt) screen.sendKey(KEYSYM.Alt_L, false);
    if (held.ctrl) screen.sendKey(KEYSYM.Control_L, false);
    if (modsRef.current.ctrl || modsRef.current.alt) {
      modsRef.current = { ctrl: false, alt: false };
      setMods(modsRef.current);
    }
  }

  function typeText(text: string) {
    for (const ch of Array.from(text)) {
      const keysym = charKeysym(ch);
      if (keysym !== null) tap(keysym);
    }
  }

  function resetBuffer() {
    typed.current = "";
    if (inputRef.current) inputRef.current.value = "";
  }

  /**
   * What the input holds now against what was already sent: the difference is sent as Backspaces then characters. It
   * covers plain typing, iOS autocorrect and predictive text (a word replaced in place) and composed characters.
   */
  function syncInput() {
    const input = inputRef.current;
    if (!input || composing.current) return;
    const before = Array.from(typed.current);
    const after = Array.from(input.value);
    let same = 0;
    while (same < before.length && same < after.length && before[same] === after[same]) same++;
    for (let i = before.length - same; i > 0; i--) tap(KEYSYM.BackSpace);
    typeText(after.slice(same).join(""));
    typed.current = input.value;
    if (after.length > BUFFER_RESET) resetBuffer();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    const special = SPECIAL_KEYS[e.key];
    if (special !== undefined) {
      e.preventDefault();
      tap(special, { ctrl: e.ctrlKey || e.metaKey, alt: e.altKey });
      if (e.key === "Enter") resetBuffer();
      return;
    }
    if (e.key === "Backspace" && e.currentTarget.value === "") {
      // Nothing left in the input to delete: the key itself goes to the computer.
      e.preventDefault();
      tap(KEYSYM.BackSpace);
      return;
    }
    // A hardware keyboard's shortcut (Ctrl/⌘ + letter): sent as Ctrl + that key, the input left untouched.
    if ((e.ctrlKey || e.metaKey || e.altKey) && e.key.length === 1) {
      const keysym = charKeysym(e.key.toLowerCase());
      if (keysym !== null) {
        e.preventDefault();
        tap(keysym, { ctrl: e.ctrlKey || e.metaKey, alt: e.altKey });
      }
    }
  }

  function toggleKeyboard() {
    const input = inputRef.current;
    if (!input) return;
    // Focused synchronously in the tap itself: iOS Safari opens its keyboard only then.
    if (document.activeElement === input) input.blur();
    else {
      resetBuffer();
      input.focus({ preventScroll: true });
    }
  }

  function sendToComputer(text: string) {
    if (!screen || !text) return;
    screen.setClipboard(text);
    setTimeout(() => tap(0x76 /* v */, { ctrl: true }), PASTE_KEY_DELAY_MS);
    flash("Texte collé sur l'ordinateur.");
  }

  async function paste() {
    if (!screen) return;
    let text: string | null = null;
    try {
      text = navigator.clipboard?.readText ? await navigator.clipboard.readText() : null;
    } catch {
      text = null;
    }
    if (text) {
      setPasteOpen(false);
      sendToComputer(text);
      return;
    }
    // Clipboard not readable here (permission refused, or no secure context): the text is pasted into a field instead.
    setPasteOpen(true);
  }

  function submitPaste(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!pasteText) return;
    sendToComputer(pasteText);
    setPasteText("");
    setPasteOpen(false);
  }

  async function copy() {
    if (!remoteClipboard) return;
    try {
      await navigator.clipboard.writeText(remoteClipboard);
      setCopied(true);
      flash("Copié sur ton appareil.");
    } catch {
      flash("Copie impossible ici : sélectionne le texte ci-dessous.");
    }
  }

  const keyButton = (label: string, keysym: number, aria?: string) => (
    <Button key={label} variant="glass" size="sm" pill className="min-w-11" disabled={!ready} aria-label={aria} onClick={() => tap(keysym)}>
      {label}
    </Button>
  );
  const modButton = (mod: Modifier, label: string) => (
    <Button
      key={mod}
      variant="glass"
      size="sm"
      pill
      className="min-w-11"
      disabled={!ready}
      aria-pressed={mods[mod]}
      onClick={() => setMods((m) => ({ ...m, [mod]: !m[mod] }))}
    >
      {label}
    </Button>
  );

  return (
    <div data-testid="remote-controls" className={cx("relative space-y-2.5", className)}>
      {/* The device's keyboard types here: kept on screen (iOS ignores focus on hidden fields), invisible, 16 px (no zoom). */}
      <input
        ref={inputRef}
        type="text"
        aria-label="Saisie vers l'ordinateur"
        data-testid="remote-keyboard-input"
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="send"
        className="pointer-events-none absolute top-0 left-1/2 h-11 w-px border-0 bg-transparent p-0 text-[16px] text-transparent caret-transparent opacity-0 outline-none"
        onFocus={() => setKeyboard(true)}
        onBlur={() => setKeyboard(false)}
        onKeyDown={onKeyDown}
        onInput={syncInput}
        onCompositionStart={() => {
          composing.current = true;
        }}
        onCompositionEnd={() => {
          composing.current = false;
          syncInput();
        }}
      />
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          variant="glass"
          pill
          disabled={!ready}
          aria-pressed={keyboard}
          // Keeps the input focused: a press on the button would blur it before the click toggles it.
          onPointerDown={(e) => e.preventDefault()}
          onClick={toggleKeyboard}
        >
          Clavier
        </Button>
        <Button variant="glass" pill disabled={!ready} onClick={() => void paste()}>
          Coller
        </Button>
        <Button variant="glass" pill disabled={!ready || !remoteClipboard} onClick={() => void copy()}>
          {copied ? <IconCheck width={15} height={15} /> : <IconCopy width={15} height={15} />}
          Copier
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-1.5" role="group" aria-label="Touches spéciales">
        {modButton("ctrl", "Ctrl")}
        {modButton("alt", "Alt")}
        {keyButton("Échap", KEYSYM.Escape)}
        {keyButton("Tab", KEYSYM.Tab)}
        {keyButton("←", KEYSYM.Left, "Flèche gauche")}
        {keyButton("↑", KEYSYM.Up, "Flèche haut")}
        {keyButton("↓", KEYSYM.Down, "Flèche bas")}
        {keyButton("→", KEYSYM.Right, "Flèche droite")}
      </div>
      {notice ? (
        <p role="status" className="text-center text-[12.5px] text-white/75">
          {notice}
        </p>
      ) : null}
      {pasteOpen ? (
        <form onSubmit={submitPaste} className="mx-auto max-w-md space-y-2 rounded-2xl border border-white/10 bg-white/5 p-3">
          <label htmlFor={pasteId} className="block text-[13px] text-white/80">
            Colle ici le texte à envoyer à l'ordinateur
          </label>
          <Textarea id={pasteId} value={pasteText} autoFocus className="min-h-20 text-[16px]" onChange={(e) => setPasteText(e.target.value)} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="glass" size="sm" pill onClick={() => setPasteOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" variant="light" size="sm" pill disabled={!pasteText}>
              Envoyer
            </Button>
          </div>
        </form>
      ) : null}
      {remoteClipboard ? (
        <div className="mx-auto max-w-md rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
          <p className="text-[11.5px] tracking-wide text-white/55 uppercase">Copié sur l'ordinateur</p>
          <p data-testid="remote-clipboard" className="mt-0.5 line-clamp-3 text-[13px] break-words whitespace-pre-wrap text-white/90 select-text">
            {remoteClipboard}
          </p>
        </div>
      ) : null}
    </div>
  );
}
